import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  TextInput,
  Dimensions,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import { dedupeCoursesForDropdown } from '../utils/coursesCatalog';

const { width } = Dimensions.get('window');
const RECORDS_PER_PAGE = 10;

const PART_OF_BODY_OPTIONS = [
  'Head',
  'Hand',
  'Forearm',
  'Elbow',
  'Chest',
  'Back',
  'Shins',
  'Toes',
  'Foot',
  'Knee',
];

const SIDE_OPTIONS = ['Left', 'Right', 'Good Side', 'Offside', 'Both', 'N/A'];

const MATERIAL_TYPE_OPTIONS = ['Tile', 'Block', 'Brick'];
const MULTIPLE_BREAKS_MATERIAL_OPTIONS = ['Brick', '3"', '4"', 'Wood 1"', 'Tile'];

export default function CreateBreaksBuilder({ navigation, route }) {
  const theme = useTheme();
  const [currentUser, setCurrentUser] = useState(null);
  const stopwatchRef = useRef(null);
  
  // Radio button state
  const [breakType, setBreakType] = useState(null); // 'course' or 'grade'
  
  // Dropdown state
  const [courses, setCourses] = useState([]);
  const [grades, setGrades] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const [showGradeDropdown, setShowGradeDropdown] = useState(false);
  
  // Table state
  const [breaks, setBreaks] = useState([]);
  const [loadingBreaks, setLoadingBreaks] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBreaks, setTotalBreaks] = useState(0);
  
  // Weapons state
  const [weapons, setWeapons] = useState([]);
  const [showWeaponDropdown, setShowWeaponDropdown] = useState(false);
  const [newWeapon, setNewWeapon] = useState('');
  const [showNewWeaponInput, setShowNewWeaponInput] = useState(false);
  
  // Form state
  const [breakName, setBreakName] = useState('');
  const [partOfBody, setPartOfBody] = useState(null);
  const [showPartOfBodyDropdown, setShowPartOfBodyDropdown] = useState(false);
  const [selectedWeapon, setSelectedWeapon] = useState(null);
  const [side, setSide] = useState(null);
  const [showSideDropdown, setShowSideDropdown] = useState(false);
  const [materialType, setMaterialType] = useState(null);
  const [showMaterialDropdown, setShowMaterialDropdown] = useState(false);
  const [height, setHeight] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [sizeInches, setSizeInches] = useState('');
  
  // Form submission
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  
  // Multiple breaks form state
  const [showMultipleBreaksForm, setShowMultipleBreaksForm] = useState(false);
  const [multipleBreaksForms, setMultipleBreaksForms] = useState([{ material: null, quantity: '1' }]);
  const [openMaterialDropdownIndex, setOpenMaterialDropdownIndex] = useState(null);

  const [builderTab, setBuilderTab] = useState('new');
  const [editingBreakId, setEditingBreakId] = useState(null);
  const [editBreaks, setEditBreaks] = useState([]);
  const [editLoadingBreaks, setEditLoadingBreaks] = useState(false);
  const [editCurrentPage, setEditCurrentPage] = useState(1);
  const [editTotalPages, setEditTotalPages] = useState(1);
  const [editTotalBreaks, setEditTotalBreaks] = useState(0);
  const [editUpdateToast, setEditUpdateToast] = useState(null);

  const closeAllDropdowns = () => {
    setShowCourseDropdown(false);
    setShowGradeDropdown(false);
    setShowPartOfBodyDropdown(false);
    setShowWeaponDropdown(false);
    setShowSideDropdown(false);
    setShowMaterialDropdown(false);
    setOpenMaterialDropdownIndex(null);
  };

  const resetFormFields = () => {
    setBreakName('');
    setPartOfBody(null);
    setSelectedWeapon(null);
    setSide(null);
    setMaterialType(null);
    setHeight('');
    setQuantity('1');
    setSizeInches('');
    setShowMultipleBreaksForm(false);
    setMultipleBreaksForms([{ material: null, quantity: '1' }]);
    setOpenMaterialDropdownIndex(null);
    setShowNewWeaponInput(false);
    setNewWeapon('');
  };

  const resetPagination = () => {
    if (builderTab === 'new') {
      setCurrentPage(1);
    } else {
      setEditCurrentPage(1);
    }
  };
  
  useEffect(() => {
    // Fetch current user data
    const fetchUser = async () => {
      const userId = route?.params?.userId || route?.params?.user?.id || route?.params?.id || 1;
      try {
        const response = await fetch(`${getApiUrl()}/api/users/${userId}`);
        if (response.ok) {
          const data = await response.json();
          setCurrentUser(data);
        } else {
          console.error('Error fetching user: HTTP', response.status);
        }
      } catch (err) {
        console.error('Error fetching user:', err);
      }
    };
    
    fetchUser();
    fetchCourses();
    fetchGrades();
    fetchWeapons();
  }, [route]);
  
  const prevBreakTypeRef = useRef(breakType);
  const prevSelectedCourseRef = useRef(selectedCourse);
  const prevSelectedGradeRef = useRef(selectedGrade);
  
  useEffect(() => {
    if (builderTab !== 'new') return;
    if (breakType !== prevBreakTypeRef.current || 
        selectedCourse !== prevSelectedCourseRef.current || 
        selectedGrade !== prevSelectedGradeRef.current) {
      setCurrentPage(1);
      prevBreakTypeRef.current = breakType;
      prevSelectedCourseRef.current = selectedCourse;
      prevSelectedGradeRef.current = selectedGrade;
    }
    
    if (breakType === 'course' && selectedCourse) {
      fetchBreaksByCourse();
    } else if (breakType === 'grade' && selectedGrade) {
      fetchBreaksByGrade();
    } else {
      setBreaks([]);
      setShowTable(false);
    }
  }, [builderTab, breakType, selectedCourse, selectedGrade, currentPage]);

  useEffect(() => {
    if (builderTab !== 'edit') return;
    if (breakType !== prevBreakTypeRef.current || 
        selectedCourse !== prevSelectedCourseRef.current || 
        selectedGrade !== prevSelectedGradeRef.current) {
      setEditCurrentPage(1);
      setEditingBreakId(null);
      resetFormFields();
      prevBreakTypeRef.current = breakType;
      prevSelectedCourseRef.current = selectedCourse;
      prevSelectedGradeRef.current = selectedGrade;
    }

    if (breakType === 'course' && selectedCourse) {
      fetchEditBreaksByCourse();
    } else if (breakType === 'grade' && selectedGrade) {
      fetchEditBreaksByGrade();
    } else {
      setEditBreaks([]);
      setEditingBreakId(null);
      resetFormFields();
    }
  }, [builderTab, breakType, selectedCourse, selectedGrade, editCurrentPage]);
  
  const fetchCourses = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/api/courses`);
      if (response.ok) {
        const data = await response.json();
        setCourses(dedupeCoursesForDropdown(Array.isArray(data) ? data : []));
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };
  
  const fetchGrades = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/api/grades`);
      if (response.ok) {
        const data = await response.json();
        setGrades(data.sort((a, b) => a.grade_rank_order - b.grade_rank_order));
      }
    } catch (error) {
      console.error('Error fetching grades:', error);
    }
  };
  
  const fetchWeapons = async () => {
    try {
      // Fetch all breaks to get distinct weapons
      const response = await fetch(`${getApiUrl()}/api/breaks`);
      if (response.ok) {
        const data = await response.json();
        const distinctWeapons = [...new Set(data.map(b => b.weapon).filter(Boolean))].sort();
        setWeapons(distinctWeapons);
      }
    } catch (error) {
      console.error('Error fetching weapons:', error);
    }
  };
  
  const fetchBreaksByCourse = async () => {
    if (!selectedCourse) return;
    
    setLoadingBreaks(true);
    try {
      const response = await fetch(
        `${getApiUrl()}/api/breaks/course/${selectedCourse.id}`
      );
      if (response.ok) {
        const data = await response.json();
        // Implement pagination
        const startIndex = (currentPage - 1) * RECORDS_PER_PAGE;
        const endIndex = startIndex + RECORDS_PER_PAGE;
        const paginatedData = data.slice(startIndex, endIndex);
        setBreaks(paginatedData);
        setTotalBreaks(data.length);
        setTotalPages(Math.ceil(data.length / RECORDS_PER_PAGE));
      }
    } catch (error) {
      console.error('Error fetching breaks by course:', error);
    } finally {
      setLoadingBreaks(false);
    }
  };
  
  const fetchBreaksByGrade = async () => {
    if (!selectedGrade) return;
    
    setLoadingBreaks(true);
    try {
      const response = await fetch(
        `${getApiUrl()}/api/breaks/grade/${encodeURIComponent(selectedGrade.grade_name)}`
      );
      if (response.ok) {
        const data = await response.json();
        // Implement pagination
        const startIndex = (currentPage - 1) * RECORDS_PER_PAGE;
        const endIndex = startIndex + RECORDS_PER_PAGE;
        const paginatedData = data.slice(startIndex, endIndex);
        setBreaks(paginatedData);
        setTotalBreaks(data.length);
        setTotalPages(Math.ceil(data.length / RECORDS_PER_PAGE));
      }
    } catch (error) {
      console.error('Error fetching breaks by grade:', error);
    } finally {
      setLoadingBreaks(false);
    }
  };

  const fetchEditBreaksByCourse = async () => {
    if (!selectedCourse) return;

    setEditLoadingBreaks(true);
    try {
      const response = await fetch(
        `${getApiUrl()}/api/breaks/course/${selectedCourse.id}`
      );
      if (response.ok) {
        const data = await response.json();
        const startIndex = (editCurrentPage - 1) * RECORDS_PER_PAGE;
        const endIndex = startIndex + RECORDS_PER_PAGE;
        const paginatedData = data.slice(startIndex, endIndex);
        setEditBreaks(paginatedData);
        setEditTotalBreaks(data.length);
        setEditTotalPages(Math.ceil(data.length / RECORDS_PER_PAGE));
      }
    } catch (error) {
      console.error('Error fetching edit breaks by course:', error);
    } finally {
      setEditLoadingBreaks(false);
    }
  };

  const fetchEditBreaksByGrade = async () => {
    if (!selectedGrade) return;

    setEditLoadingBreaks(true);
    try {
      const response = await fetch(
        `${getApiUrl()}/api/breaks/grade/${encodeURIComponent(selectedGrade.grade_name)}`
      );
      if (response.ok) {
        const data = await response.json();
        const startIndex = (editCurrentPage - 1) * RECORDS_PER_PAGE;
        const endIndex = startIndex + RECORDS_PER_PAGE;
        const paginatedData = data.slice(startIndex, endIndex);
        setEditBreaks(paginatedData);
        setEditTotalBreaks(data.length);
        setEditTotalPages(Math.ceil(data.length / RECORDS_PER_PAGE));
      }
    } catch (error) {
      console.error('Error fetching edit breaks by grade:', error);
    } finally {
      setEditLoadingBreaks(false);
    }
  };

  const switchBuilderTab = (tab) => {
    if (tab === builderTab) return;
    setBuilderTab(tab);
    setBreakType(null);
    setSelectedCourse(null);
    setSelectedGrade(null);
    setBreaks([]);
    setEditBreaks([]);
    setShowTable(false);
    setCurrentPage(1);
    setEditCurrentPage(1);
    setEditingBreakId(null);
    resetFormFields();
    setShowSuccessMessage(false);
    setEditUpdateToast(null);
    closeAllDropdowns();
  };

  const populateFormFromBreak = (breakItem) => {
    setBreakName(breakItem.break_name || '');
    setPartOfBody(breakItem.part_of_body || null);
    setSelectedWeapon(breakItem.weapon || null);
    setSide(breakItem.side || null);
    setMaterialType(breakItem.material_type || null);
    setHeight(breakItem.height != null ? String(breakItem.height) : '');
    setQuantity(breakItem.quantity != null ? String(breakItem.quantity) : '1');
    setSizeInches(breakItem.size_inches != null ? String(breakItem.size_inches) : '');
    setShowMultipleBreaksForm(false);
    setMultipleBreaksForms([{ material: null, quantity: '1' }]);
  };

  const handleEditBreakSelect = (breakItem) => {
    setEditingBreakId(breakItem.id);
    populateFormFromBreak(breakItem);
    closeAllDropdowns();
  };

  const handleEditCancel = () => {
    setEditingBreakId(null);
    resetFormFields();
    setEditUpdateToast(null);
  };

  const handleUpdateBreak = async () => {
    if (!editingBreakId) return;

    if (!breakName.trim()) {
      alert('Break Name is required');
      return;
    }

    setSubmitting(true);
    try {
      const breakData = {
        break_name: breakName.trim(),
        part_of_body: partOfBody || null,
        weapon: selectedWeapon || null,
        side: side || null,
        material_type: materialType || null,
        height: height ? parseInt(height, 10) : null,
        quantity: quantity ? parseInt(quantity, 10) : 1,
        size_inches: sizeInches ? parseFloat(sizeInches) : null,
      };

      if (breakType === 'grade' && selectedGrade) {
        breakData.grade = selectedGrade.grade_name;
      }

      const response = await fetch(`${getApiUrl()}/api/breaks/${editingBreakId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(breakData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update break');
      }

      setEditUpdateToast({ type: 'success', message: 'Break updated successfully.' });

      if (breakType === 'course') {
        fetchEditBreaksByCourse();
      } else {
        fetchEditBreaksByGrade();
      }

      setTimeout(() => setEditUpdateToast(null), 5000);
    } catch (error) {
      console.error('Error updating break:', error);
      setEditUpdateToast({
        type: 'error',
        message: error.message || 'Failed to update break',
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleCreateBreak = async () => {
    if (!breakName.trim()) {
      alert('Break Name is required');
      return;
    }
    
    if (!breakType) {
      alert('Please select Course break or Grade break');
      return;
    }
    
    if (breakType === 'course' && !selectedCourse) {
      alert('Please select a course');
      return;
    }
    
    if (breakType === 'grade' && !selectedGrade) {
      alert('Please select a grade');
      return;
    }
    
    setSubmitting(true);
    try {
      // Create the break
      const breakData = {
        break_name: breakName.trim(),
        part_of_body: partOfBody || null,
        weapon: selectedWeapon || null,
        side: side || null,
        material_type: materialType || null,
        height: height ? parseInt(height) : null,
        quantity: quantity ? parseInt(quantity) : 1,
        size_inches: sizeInches ? parseFloat(sizeInches) : null,
      };
      
      const breakResponse = await fetch(`${getApiUrl()}/api/breaks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(breakData),
      });
      
      if (!breakResponse.ok) {
        const errorData = await breakResponse.json();
        throw new Error(errorData.error || 'Failed to create break');
      }
      
      const newBreak = await breakResponse.json();
      
      // Link break to course or grade
      if (breakType === 'course') {
        const linkResponse = await fetch(`${getApiUrl()}/api/course-breaks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            course_id: selectedCourse.id,
            break_id: newBreak.id,
          }),
        });
        
        if (!linkResponse.ok) {
          throw new Error('Failed to link break to course');
        }
      } else if (breakType === 'grade') {
        const linkResponse = await fetch(`${getApiUrl()}/api/grade-breaks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            grade: selectedGrade.grade_name,
            break_id: newBreak.id,
          }),
        });
        
        if (!linkResponse.ok) {
          throw new Error('Failed to link break to grade');
        }
        
        // Update break grade field (denormalized field for easier filtering)
        await fetch(`${getApiUrl()}/api/breaks/${newBreak.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            grade: selectedGrade.grade_name,
          }),
        });
      }
      
      // Process additional breaks from multiple breaks form
      if (showMultipleBreaksForm && multipleBreaksForms.length > 0) {
        for (const form of multipleBreaksForms) {
          if (!form.material || !form.quantity) {
            continue; // Skip incomplete forms
          }
          
          // Parse material and calculate values
          let materialType = null;
          let sizeInchesValue = null;
          let totalSizeInches = null;
          const quantityValue = parseInt(form.quantity) || 1;
          
          if (form.material === 'Brick') {
            materialType = 'Brick';
            totalSizeInches = quantityValue.toString();
          } else if (form.material === '3"') {
            materialType = 'Block';
            sizeInchesValue = 3;
            totalSizeInches = (quantityValue * 3).toString();
          } else if (form.material === '4"') {
            materialType = 'Block';
            sizeInchesValue = 4;
            totalSizeInches = (quantityValue * 4).toString();
          } else if (form.material === 'Wood 1"') {
            materialType = 'Block'; // Wood is stored as Block type
            sizeInchesValue = 1;
            totalSizeInches = (quantityValue * 1).toString();
          } else if (form.material === 'Tile') {
            materialType = 'Tile';
            totalSizeInches = quantityValue.toString();
          }
          
          // Create additional break
          const additionalBreakData = {
            break_name: breakName.trim(),
            part_of_body: partOfBody || null,
            weapon: selectedWeapon || null,
            side: side || null,
            material_type: materialType,
            height: height ? parseInt(height) : null,
            quantity: quantityValue,
            size_inches: sizeInchesValue,
            total_size_inches: totalSizeInches,
          };
          
          const additionalBreakResponse = await fetch(`${getApiUrl()}/api/breaks`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(additionalBreakData),
          });
          
          if (!additionalBreakResponse.ok) {
            const errorData = await additionalBreakResponse.json();
            console.error(`Failed to create additional break: ${errorData.error}`);
            continue; // Continue with next break even if one fails
          }
          
          const newAdditionalBreak = await additionalBreakResponse.json();
          
          // Link additional break to course or grade
          if (breakType === 'course') {
            await fetch(`${getApiUrl()}/api/course-breaks`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                course_id: selectedCourse.id,
                break_id: newAdditionalBreak.id,
              }),
            });
          } else if (breakType === 'grade') {
            await fetch(`${getApiUrl()}/api/grade-breaks`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                grade: selectedGrade.grade_name,
                break_id: newAdditionalBreak.id,
              }),
            });
            
            // Update break grade field
            await fetch(`${getApiUrl()}/api/breaks/${newAdditionalBreak.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                grade: selectedGrade.grade_name,
              }),
            });
          }
        }
      }
      
      // Show success message
      setShowSuccessMessage(true);
      
      // Reset form
      resetFormFields();
      
      // Refresh breaks list
      if (breakType === 'course') {
        fetchBreaksByCourse();
      } else {
        fetchBreaksByGrade();
      }
      
      // Hide success message after 5 seconds
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);
    } catch (error) {
      console.error('Error creating break:', error);
      alert(`Error creating break: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleAddWeapon = () => {
    if (!newWeapon.trim()) {
      alert('Please enter a weapon name');
      return;
    }
    
    if (weapons.includes(newWeapon.trim())) {
      alert('This weapon already exists');
      return;
    }
    
    setWeapons([...weapons, newWeapon.trim()].sort());
    setSelectedWeapon(newWeapon.trim());
    setNewWeapon('');
    setShowNewWeaponInput(false);
  };
  
  const renderTable = () => {
    if (!showTable) return null;
    
    // Show loading state
    if (loadingBreaks) {
      return (
        <View style={styles.tableContainer}>
          <View style={styles.emptyTableMessage}>
            <Text style={styles.emptyTableText}>Loading...</Text>
          </View>
        </View>
      );
    }
    
    // Show empty state
    if (breaks.length === 0) {
      const emptyMessage = breakType === 'course' && selectedCourse
        ? `There are no breaks for this particular course.`
        : breakType === 'grade' && selectedGrade
        ? `There are no breaks for this particular grade.`
        : 'No breaks found.';
      
      return (
        <View style={styles.tableContainer}>
          <View style={styles.emptyTableMessage}>
            <Text style={styles.emptyTableText}>{emptyMessage}</Text>
          </View>
        </View>
      );
    }
    
    return (
      <View style={styles.tableContainer}>
        <View style={styles.tableHeader}>
          <View style={styles.tableHeaderCell}>
            <Text style={styles.tableHeaderText}>Break Name</Text>
          </View>
          <View style={styles.tableHeaderCell}>
            <Text style={styles.tableHeaderText}>Part of Body</Text>
          </View>
          <View style={styles.tableHeaderCell}>
            <Text style={styles.tableHeaderText}>Weapon</Text>
          </View>
          <View style={styles.tableHeaderCell}>
            <Text style={styles.tableHeaderText}>Side</Text>
          </View>
          <View style={styles.tableHeaderCell}>
            <Text style={styles.tableHeaderText}>Material</Text>
          </View>
        </View>
        
        <ScrollView style={styles.tableBody}>
          {breaks.map((breakItem) => (
            <View key={breakItem.id} style={styles.tableRow}>
              <View style={styles.tableCell}>
                <Text style={styles.tableCellText}>{breakItem.break_name || ''}</Text>
              </View>
              <View style={styles.tableCell}>
                <Text style={styles.tableCellText}>{breakItem.part_of_body || ''}</Text>
              </View>
              <View style={styles.tableCell}>
                <Text style={styles.tableCellText}>{breakItem.weapon || ''}</Text>
              </View>
              <View style={styles.tableCell}>
                <Text style={styles.tableCellText}>{breakItem.side || ''}</Text>
              </View>
              <View style={styles.tableCell}>
                <Text style={styles.tableCellText}>{breakItem.material_type || ''}</Text>
              </View>
            </View>
          ))}
        </ScrollView>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <View style={styles.paginationContainer}>
            <TouchableOpacity
              style={[styles.paginationButton, currentPage === 1 && styles.paginationButtonDisabled]}
              onPress={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              <Text style={styles.paginationButtonText}>Previous</Text>
            </TouchableOpacity>
            
            <Text style={styles.paginationText}>
              Page {currentPage} of {totalPages} ({totalBreaks} total)
            </Text>
            
            <TouchableOpacity
              style={[styles.paginationButton, currentPage === totalPages && styles.paginationButtonDisabled]}
              onPress={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
            >
              <Text style={styles.paginationButtonText}>Next</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    );
  };

  const renderBreakTypeSelection = () => (
    <>
      <View style={styles.radioGroup}>
        <TouchableOpacity
          style={styles.radioOption}
          onPress={() => {
            setBreakType('course');
            setSelectedGrade(null);
            setShowTable(false);
            resetPagination();
          }}
        >
          <View style={styles.radioCircle}>
            {breakType === 'course' && <View style={styles.radioInner} />}
          </View>
          <Text style={styles.radioLabel}>Course break</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.radioOption}
          onPress={() => {
            setBreakType('grade');
            setSelectedCourse(null);
            setShowTable(false);
            resetPagination();
          }}
        >
          <View style={styles.radioCircle}>
            {breakType === 'grade' && <View style={styles.radioInner} />}
          </View>
          <Text style={styles.radioLabel}>Grade break</Text>
        </TouchableOpacity>
      </View>

      {breakType === 'course' && (
        <View style={styles.dropdownContainer}>
          <Text style={styles.label}>Select Course</Text>
          <TouchableOpacity
            style={styles.dropdownButton}
            onPress={() => {
              setShowCourseDropdown(!showCourseDropdown);
              setShowGradeDropdown(false);
            }}
          >
            <Text style={styles.dropdownButtonText}>
              {selectedCourse ? selectedCourse.course_name : 'Select Course'}
            </Text>
            <Text style={styles.dropdownArrow}>
              {showCourseDropdown ? '▲' : '▼'}
            </Text>
          </TouchableOpacity>

          {showCourseDropdown && (
            <>
              {Platform.OS === 'web' && (
                <TouchableOpacity
                  style={styles.dropdownOverlay}
                  activeOpacity={1}
                  onPress={() => setShowCourseDropdown(false)}
                />
              )}
              <View style={styles.dropdownMenu}>
                <ScrollView style={styles.dropdownScrollView}>
                  {courses.map((course) => (
                    <TouchableOpacity
                      key={course.id}
                      style={styles.dropdownOption}
                      onPress={() => {
                        setSelectedCourse(course);
                        setShowCourseDropdown(false);
                        resetPagination();
                      }}
                    >
                      <Text style={styles.dropdownOptionText}>
                        {course.course_name}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            </>
          )}
        </View>
      )}

      {breakType === 'grade' && (
        <View style={styles.dropdownContainer}>
          <Text style={styles.label}>Select Grade</Text>
          <TouchableOpacity
            style={styles.dropdownButton}
            onPress={() => {
              setShowGradeDropdown(!showGradeDropdown);
              setShowCourseDropdown(false);
            }}
          >
            <Text style={styles.dropdownButtonText}>
              {selectedGrade ? selectedGrade.grade_name : 'Select Grade'}
            </Text>
            <Text style={styles.dropdownArrow}>
              {showGradeDropdown ? '▲' : '▼'}
            </Text>
          </TouchableOpacity>

          {showGradeDropdown && (
            <>
              {Platform.OS === 'web' && (
                <TouchableOpacity
                  style={styles.dropdownOverlay}
                  activeOpacity={1}
                  onPress={() => setShowGradeDropdown(false)}
                />
              )}
              <View style={styles.dropdownMenu}>
                <ScrollView style={styles.dropdownScrollView}>
                  {grades.map((grade) => (
                    <TouchableOpacity
                      key={grade.id}
                      style={styles.dropdownOption}
                      onPress={() => {
                        setSelectedGrade(grade);
                        setShowGradeDropdown(false);
                        resetPagination();
                      }}
                    >
                      <Text style={styles.dropdownOptionText}>
                        {grade.grade_name}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            </>
          )}
        </View>
      )}
    </>
  );

  const renderEditTable = () => {
    const hasSelection =
      (breakType === 'course' && selectedCourse) ||
      (breakType === 'grade' && selectedGrade);

    if (!hasSelection) return null;

    if (editLoadingBreaks) {
      return (
        <View style={styles.tableContainer}>
          <View style={styles.emptyTableMessage}>
            <Text style={styles.emptyTableText}>Loading...</Text>
          </View>
        </View>
      );
    }

    if (editBreaks.length === 0) {
      const emptyMessage =
        breakType === 'course' && selectedCourse
          ? 'There are no breaks for this particular course.'
          : breakType === 'grade' && selectedGrade
          ? 'There are no breaks for this particular grade.'
          : 'No breaks found.';

      return (
        <View style={styles.tableContainer}>
          <View style={styles.emptyTableMessage}>
            <Text style={styles.emptyTableText}>{emptyMessage}</Text>
          </View>
        </View>
      );
    }

    return (
      <View style={styles.tableContainer}>
        <Text style={styles.editTableHint}>Select a break below to edit.</Text>
        <View style={styles.tableHeader}>
          <View style={styles.tableHeaderCell}>
            <Text style={styles.tableHeaderText}>Break Name</Text>
          </View>
          <View style={styles.tableHeaderCell}>
            <Text style={styles.tableHeaderText}>Part of Body</Text>
          </View>
          <View style={styles.tableHeaderCell}>
            <Text style={styles.tableHeaderText}>Weapon</Text>
          </View>
          <View style={styles.tableHeaderCell}>
            <Text style={styles.tableHeaderText}>Side</Text>
          </View>
          <View style={styles.tableHeaderCell}>
            <Text style={styles.tableHeaderText}>Material</Text>
          </View>
        </View>

        <ScrollView style={styles.tableBody}>
          {editBreaks.map((breakItem) => {
            const isSelected = editingBreakId === breakItem.id;
            return (
              <TouchableOpacity
                key={breakItem.id}
                style={[styles.tableRow, isSelected && styles.tableRowSelected]}
                onPress={() => handleEditBreakSelect(breakItem)}
              >
                <View style={styles.tableCell}>
                  <Text style={styles.tableCellText}>{breakItem.break_name || ''}</Text>
                </View>
                <View style={styles.tableCell}>
                  <Text style={styles.tableCellText}>{breakItem.part_of_body || ''}</Text>
                </View>
                <View style={styles.tableCell}>
                  <Text style={styles.tableCellText}>{breakItem.weapon || ''}</Text>
                </View>
                <View style={styles.tableCell}>
                  <Text style={styles.tableCellText}>{breakItem.side || ''}</Text>
                </View>
                <View style={styles.tableCell}>
                  <Text style={styles.tableCellText}>{breakItem.material_type || ''}</Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </ScrollView>

        {editTotalPages > 1 && (
          <View style={styles.paginationContainer}>
            <TouchableOpacity
              style={[
                styles.paginationButton,
                editCurrentPage === 1 && styles.paginationButtonDisabled,
              ]}
              onPress={() => setEditCurrentPage(Math.max(1, editCurrentPage - 1))}
              disabled={editCurrentPage === 1}
            >
              <Text style={styles.paginationButtonText}>Previous</Text>
            </TouchableOpacity>

            <Text style={styles.paginationText}>
              Page {editCurrentPage} of {editTotalPages} ({editTotalBreaks} total)
            </Text>

            <TouchableOpacity
              style={[
                styles.paginationButton,
                editCurrentPage === editTotalPages && styles.paginationButtonDisabled,
              ]}
              onPress={() =>
                setEditCurrentPage(Math.min(editTotalPages, editCurrentPage + 1))
              }
              disabled={editCurrentPage === editTotalPages}
            >
              <Text style={styles.paginationButtonText}>Next</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    );
  };
  
  const styles = getStyles(theme);
  
  return (
    <View style={styles.container}>
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>Breaks Builder</Text>
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>
      
      <ScrollView 
        style={styles.content} 
        contentContainerStyle={Platform.OS === 'web' ? { overflow: 'visible' } : {}}
        showsVerticalScrollIndicator={true}
      >
        <View style={styles.titleSection}>
          <Text style={styles.title}>Breaks Builder</Text>
          <Text style={styles.subtitle}>
            Use this form to create or edit breaks for a grade or a course. Every break should be under one of those. Filter for that course or grade and if you cannot see the particular break, create it in the form below.{'\n'}
            5 bricks with hammerfist - will still be under Iron Fist.{'\n'}
            headbutt would be under Iron Head, etc.
          </Text>
        </View>

        <View style={styles.pageTabRow}>
          <TouchableOpacity
            style={[styles.pageTab, builderTab === 'new' && styles.pageTabOn]}
            onPress={() => switchBuilderTab('new')}
            disabled={submitting}
          >
            <Text style={[styles.pageTabText, builderTab === 'new' && styles.pageTabTextOn]}>
              New
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.pageTab, builderTab === 'edit' && styles.pageTabOn]}
            onPress={() => switchBuilderTab('edit')}
            disabled={submitting}
          >
            <Text style={[styles.pageTabText, builderTab === 'edit' && styles.pageTabTextOn]}>
              Edit
            </Text>
          </TouchableOpacity>
        </View>

        {builderTab === 'new' ? (
          <>
            {renderBreakTypeSelection()}

            {((breakType === 'course' && selectedCourse) || (breakType === 'grade' && selectedGrade)) && (
              <TouchableOpacity
                style={styles.toggleButton}
                onPress={() => setShowTable(!showTable)}
              >
                <Text style={styles.toggleButtonText}>
                  {showTable ? 'Hide' : 'Show'} Breaks Table
                </Text>
              </TouchableOpacity>
            )}

            {renderTable()}
          </>
        ) : (
          <>
            {renderBreakTypeSelection()}
            {renderEditTable()}
          </>
        )}

        {(builderTab === 'new' || editingBreakId) && (
        <View style={[styles.formContainer, Platform.OS === 'web' && { overflow: 'visible' }]}>
          <Text style={styles.formTitle}>
            {builderTab === 'edit' ? 'Edit Break' : 'Create New Break'}
          </Text>
          
          {/* Break Name */}
          <View style={styles.formField}>
            <Text style={styles.label}>Break Name *</Text>
            <TextInput
              style={styles.input}
              value={breakName}
              onChangeText={setBreakName}
              placeholder="Enter break name"
              placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
            />
          </View>
          
          {/* Part of Body */}
          <View style={[styles.formField, showPartOfBodyDropdown && styles.formFieldDropdownOpen]}>
            <Text style={styles.label}>Part of Body</Text>
            <TouchableOpacity
              style={styles.dropdownButton}
              onPress={() => {
                setShowPartOfBodyDropdown(!showPartOfBodyDropdown);
                setShowSideDropdown(false);
                setShowMaterialDropdown(false);
                setShowWeaponDropdown(false);
              }}
            >
              <Text style={styles.dropdownButtonText}>
                {partOfBody || 'Select Part of Body'}
              </Text>
              <Text style={styles.dropdownArrow}>
                {showPartOfBodyDropdown ? '▲' : '▼'}
              </Text>
            </TouchableOpacity>
            
            {showPartOfBodyDropdown && (
              <>
                {Platform.OS === 'web' && (
                  <TouchableOpacity
                    style={styles.dropdownOverlay}
                    activeOpacity={1}
                    onPress={() => setShowPartOfBodyDropdown(false)}
                  />
                )}
                <View style={styles.dropdownMenu}>
                  <ScrollView style={styles.dropdownScrollView}>
                    <TouchableOpacity
                      style={styles.dropdownOption}
                      onPress={() => {
                        setPartOfBody(null);
                        setShowPartOfBodyDropdown(false);
                      }}
                    >
                      <Text style={styles.dropdownOptionText}>None</Text>
                    </TouchableOpacity>
                    {PART_OF_BODY_OPTIONS.map((part) => (
                      <TouchableOpacity
                        key={part}
                        style={styles.dropdownOption}
                        onPress={() => {
                          setPartOfBody(part);
                          setShowPartOfBodyDropdown(false);
                        }}
                      >
                        <Text style={styles.dropdownOptionText}>{part}</Text>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                </View>
              </>
            )}
          </View>
          
          {/* Weapon */}
          <View style={[styles.formField, showWeaponDropdown && styles.formFieldDropdownOpen]}>
            <Text style={styles.label}>Weapon</Text>
            <TouchableOpacity
              style={styles.dropdownButton}
              onPress={() => {
                setShowWeaponDropdown(!showWeaponDropdown);
                setShowPartOfBodyDropdown(false);
                setShowSideDropdown(false);
                setShowMaterialDropdown(false);
              }}
            >
              <Text style={styles.dropdownButtonText}>
                {selectedWeapon || 'Select Weapon'}
              </Text>
              <Text style={styles.dropdownArrow}>
                {showWeaponDropdown ? '▲' : '▼'}
              </Text>
            </TouchableOpacity>
            
            {showWeaponDropdown && (
              <>
                {Platform.OS === 'web' && (
                  <TouchableOpacity
                    style={styles.dropdownOverlay}
                    activeOpacity={1}
                    onPress={() => setShowWeaponDropdown(false)}
                  />
                )}
                <View style={styles.dropdownMenu}>
                  <ScrollView style={styles.dropdownScrollView}>
                    <TouchableOpacity
                      style={styles.dropdownOption}
                      onPress={() => {
                        setSelectedWeapon(null);
                        setShowWeaponDropdown(false);
                      }}
                    >
                      <Text style={styles.dropdownOptionText}>None</Text>
                    </TouchableOpacity>
                    {weapons.map((weapon) => (
                      <TouchableOpacity
                        key={weapon}
                        style={styles.dropdownOption}
                        onPress={() => {
                          setSelectedWeapon(weapon);
                          setShowWeaponDropdown(false);
                        }}
                      >
                        <Text style={styles.dropdownOptionText}>{weapon}</Text>
                      </TouchableOpacity>
                    ))}
                    <TouchableOpacity
                      style={[styles.dropdownOption, styles.createNewOption]}
                      onPress={() => {
                        setShowNewWeaponInput(true);
                        setShowWeaponDropdown(false);
                      }}
                    >
                      <Text style={styles.createNewText}>+ Create New Weapon</Text>
                    </TouchableOpacity>
                  </ScrollView>
                </View>
              </>
            )}
            
            {showNewWeaponInput && (
              <View style={styles.newWeaponContainer}>
                <TextInput
                  style={styles.input}
                  value={newWeapon}
                  onChangeText={setNewWeapon}
                  placeholder="Enter new weapon name"
                  placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                />
                <View style={styles.newWeaponButtons}>
                  <TouchableOpacity
                    style={styles.addWeaponButton}
                    onPress={handleAddWeapon}
                  >
                    <Text style={styles.addWeaponButtonText}>Add</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.cancelWeaponButton}
                    onPress={() => {
                      setShowNewWeaponInput(false);
                      setNewWeapon('');
                    }}
                  >
                    <Text style={styles.cancelWeaponButtonText}>Cancel</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </View>
          
          {/* Side */}
          <View style={[styles.formField, showSideDropdown && styles.formFieldDropdownOpen]}>
            <Text style={styles.label}>Side</Text>
            <TouchableOpacity
              style={styles.dropdownButton}
              onPress={() => {
                setShowSideDropdown(!showSideDropdown);
                setShowPartOfBodyDropdown(false);
                setShowMaterialDropdown(false);
                setShowWeaponDropdown(false);
              }}
            >
              <Text style={styles.dropdownButtonText}>
                {side || 'Select Side'}
              </Text>
              <Text style={styles.dropdownArrow}>
                {showSideDropdown ? '▲' : '▼'}
              </Text>
            </TouchableOpacity>
            
            {showSideDropdown && (
              <>
                {Platform.OS === 'web' && (
                  <TouchableOpacity
                    style={styles.dropdownOverlay}
                    activeOpacity={1}
                    onPress={() => setShowSideDropdown(false)}
                  />
                )}
                <View style={styles.dropdownMenu}>
                  <ScrollView style={styles.dropdownScrollView}>
                    <TouchableOpacity
                      style={styles.dropdownOption}
                      onPress={() => {
                        setSide(null);
                        setShowSideDropdown(false);
                      }}
                    >
                      <Text style={styles.dropdownOptionText}>None</Text>
                    </TouchableOpacity>
                    {SIDE_OPTIONS.map((s) => (
                      <TouchableOpacity
                        key={s}
                        style={styles.dropdownOption}
                        onPress={() => {
                          setSide(s);
                          setShowSideDropdown(false);
                        }}
                      >
                        <Text style={styles.dropdownOptionText}>{s}</Text>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                </View>
              </>
            )}
          </View>
          
          {/* Material Type */}
          <View style={[styles.formField, showMaterialDropdown && styles.formFieldDropdownOpen]}>
            <Text style={styles.label}>Material Type</Text>
            <TouchableOpacity
              style={styles.dropdownButton}
              onPress={() => {
                setShowMaterialDropdown(!showMaterialDropdown);
                setShowPartOfBodyDropdown(false);
                setShowSideDropdown(false);
                setShowWeaponDropdown(false);
              }}
            >
              <Text style={styles.dropdownButtonText}>
                {materialType || 'Select Material Type'}
              </Text>
              <Text style={styles.dropdownArrow}>
                {showMaterialDropdown ? '▲' : '▼'}
              </Text>
            </TouchableOpacity>
            
            {showMaterialDropdown && (
              <>
                {Platform.OS === 'web' && (
                  <TouchableOpacity
                    style={styles.dropdownOverlay}
                    activeOpacity={1}
                    onPress={() => setShowMaterialDropdown(false)}
                  />
                )}
                <View style={styles.dropdownMenu}>
                  <ScrollView style={styles.dropdownScrollView}>
                    <TouchableOpacity
                      style={styles.dropdownOption}
                      onPress={() => {
                        setMaterialType(null);
                        setShowMaterialDropdown(false);
                      }}
                    >
                      <Text style={styles.dropdownOptionText}>None</Text>
                    </TouchableOpacity>
                    {MATERIAL_TYPE_OPTIONS.map((material) => (
                      <TouchableOpacity
                        key={material}
                        style={styles.dropdownOption}
                        onPress={() => {
                          setMaterialType(material);
                          setShowMaterialDropdown(false);
                        }}
                      >
                        <Text style={styles.dropdownOptionText}>{material}</Text>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                </View>
              </>
            )}
          </View>
          
          {/* Height */}
          <View style={styles.formField}>
            <Text style={styles.label}>Height</Text>
            <TextInput
              style={styles.input}
              value={height}
              onChangeText={setHeight}
              placeholder="Enter height"
              placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
              keyboardType="numeric"
            />
          </View>
          
          {/* Quantity */}
          <View style={styles.formField}>
            <Text style={styles.label}>Quantity</Text>
            <TextInput
              style={styles.input}
              value={quantity}
              onChangeText={setQuantity}
              placeholder="Enter quantity (default: 1)"
              placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
              keyboardType="numeric"
            />
          </View>
          
          {/* Size (Inches) */}
          <View style={styles.formField}>
            <Text style={styles.label}>Size (Inches)</Text>
            <TextInput
              style={styles.input}
              value={sizeInches}
              onChangeText={setSizeInches}
              placeholder="Enter size in inches"
              placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
              keyboardType="numeric"
            />
          </View>
          
          {/* Add Multiple Breaks Button */}
          {builderTab === 'new' && (
          <TouchableOpacity
            style={styles.addMultipleButton}
            onPress={() => {
              setShowMultipleBreaksForm(!showMultipleBreaksForm);
              setOpenMaterialDropdownIndex(null);
              if (!showMultipleBreaksForm) {
                setMultipleBreaksForms([{ material: null, quantity: '1' }]);
              }
            }}
          >
            <Text style={styles.addMultipleButtonText}>
              {showMultipleBreaksForm ? 'Hide Multiple Breaks Form' : 'Add Multiple of This Break'}
            </Text>
          </TouchableOpacity>
          )}

          {/* Multiple Breaks Form */}
          {builderTab === 'new' && showMultipleBreaksForm && (
            <View style={styles.multipleBreaksFormContainer}>
              {multipleBreaksForms.map((form, index) => (
                <View key={index} style={styles.multipleBreakFormRow}>
                  <View style={[styles.multipleBreakFormField, openMaterialDropdownIndex === index && styles.formFieldDropdownOpen]}>
                    <Text style={styles.label}>Material</Text>
                    <TouchableOpacity
                      style={styles.dropdownButton}
                      onPress={() => {
                        setOpenMaterialDropdownIndex(openMaterialDropdownIndex === index ? null : index);
                      }}
                    >
                      <Text style={styles.dropdownButtonText}>
                        {form.material || 'Select Material'}
                      </Text>
                      <Text style={styles.dropdownArrow}>
                        {openMaterialDropdownIndex === index ? '▲' : '▼'}
                      </Text>
                    </TouchableOpacity>
                    {/* Material Dropdown */}
                    {openMaterialDropdownIndex === index && (
                      <>
                        {Platform.OS === 'web' && (
                          <TouchableOpacity
                            style={styles.dropdownOverlay}
                            activeOpacity={1}
                            onPress={() => setOpenMaterialDropdownIndex(null)}
                          />
                        )}
                        <View style={styles.dropdownMenu}>
                          <ScrollView style={styles.dropdownScrollView}>
                            {MULTIPLE_BREAKS_MATERIAL_OPTIONS.map((material) => (
                              <TouchableOpacity
                                key={material}
                                style={styles.dropdownOption}
                                onPress={() => {
                                  const updatedForms = [...multipleBreaksForms];
                                  updatedForms[index].material = material;
                                  setMultipleBreaksForms(updatedForms);
                                  setOpenMaterialDropdownIndex(null);
                                }}
                              >
                                <Text style={styles.dropdownOptionText}>{material}</Text>
                              </TouchableOpacity>
                            ))}
                          </ScrollView>
                        </View>
                      </>
                    )}
                  </View>
                  
                  <View style={styles.multipleBreakFormField}>
                    <Text style={styles.label}>Quantity</Text>
                    <TextInput
                      style={styles.input}
                      value={form.quantity}
                      onChangeText={(text) => {
                        const updatedForms = [...multipleBreaksForms];
                        updatedForms[index].quantity = text;
                        setMultipleBreaksForms(updatedForms);
                      }}
                      placeholder="Enter quantity"
                      placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                      keyboardType="numeric"
                    />
                  </View>
                  
                  <TouchableOpacity
                    style={styles.addFormRowButton}
                    onPress={() => {
                      setMultipleBreaksForms([...multipleBreaksForms, { material: null, quantity: '1' }]);
                    }}
                  >
                    <Text style={styles.addFormRowButtonText}>+</Text>
                  </TouchableOpacity>
                </View>
              ))}
            </View>
          )}

          {builderTab === 'new' ? (
            <>
              <TouchableOpacity
                style={[styles.submitButton, submitting && styles.submitButtonDisabled]}
                onPress={handleCreateBreak}
                disabled={submitting}
              >
                <Text style={styles.submitButtonText}>
                  {submitting ? 'Creating...' : 'Create Break'}
                </Text>
              </TouchableOpacity>

              {showSuccessMessage && (
                <View style={styles.successMessageContainer}>
                  <Text style={styles.successMessageText}>
                    The record has been created successfully.
                  </Text>
                </View>
              )}
            </>
          ) : (
            <>
              <View style={styles.editActionRow}>
                <TouchableOpacity
                  style={[styles.submitButton, styles.editUpdateButton, submitting && styles.submitButtonDisabled]}
                  onPress={handleUpdateBreak}
                  disabled={submitting}
                >
                  <Text style={styles.submitButtonText}>
                    {submitting ? 'Updating...' : 'Update Break'}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.cancelEditButton, submitting && styles.submitButtonDisabled]}
                  onPress={handleEditCancel}
                  disabled={submitting}
                >
                  <Text style={styles.cancelEditButtonText}>Cancel</Text>
                </TouchableOpacity>
              </View>

              {editUpdateToast ? (
                <View
                  style={[
                    styles.successMessageContainer,
                    editUpdateToast.type === 'error' && styles.errorMessageContainer,
                  ]}
                >
                  <Text
                    style={[
                      styles.successMessageText,
                      editUpdateToast.type === 'error' && styles.errorMessageText,
                    ]}
                  >
                    {editUpdateToast.message}
                  </Text>
                </View>
              ) : null}
            </>
          )}
        </View>
        )}
      </ScrollView>
      
      {/* Stopwatch Component */}
      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const getStyles = (theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 50,
    paddingBottom: 20,
    paddingHorizontal: 20,
    backgroundColor: 'transparent',
  },
  hamburgerContainer: {
    marginRight: 15,
  },
  breadcrumbContainer: {
    flex: 1,
  },
  breadcrumb: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.heading,
  },
  profileIconContainer: {
    backgroundColor: 'transparent',
  },
  content: {
    flex: 1,
    paddingHorizontal: 20,
    ...(Platform.OS === 'web' ? {
      overflow: 'visible',
    } : {}),
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    color: theme.isDarkMode ? '#d4af37' : '#000000',
    marginBottom: 10,
    textAlign: 'center',
    fontFamily: Platform.OS === 'web' ? 'serif' : undefined,
    backgroundColor: 'transparent',
  },
  titleSection: {
    marginBottom: 30,
    alignItems: 'center',
  },
  pageTabRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 24,
    alignSelf: 'stretch',
  },
  pageTab: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    backgroundColor: theme.isDarkMode ? '#3a3a3a' : '#E8E8E8',
  },
  pageTabOn: {
    backgroundColor: theme.colors.primary || '#d4af37',
  },
  pageTabText: {
    fontSize: 16,
    fontWeight: '700',
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  pageTabTextOn: {
    color: '#000',
    backgroundColor: 'transparent',
  },
  editTableHint: {
    fontSize: 14,
    color: theme.colors.text,
    marginBottom: 12,
    opacity: 0.85,
    backgroundColor: 'transparent',
  },
  tableRowSelected: {
    backgroundColor: theme.isDarkMode ? 'rgba(212, 175, 55, 0.2)' : 'rgba(212, 175, 55, 0.15)',
  },
  editActionRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  editUpdateButton: {
    flex: 1,
    marginTop: 0,
  },
  cancelEditButton: {
    flex: 1,
    paddingVertical: 15,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: theme.colors.border,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
  },
  cancelEditButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  errorMessageContainer: {
    backgroundColor: theme.isDarkMode ? '#4a2020' : '#FFEBEE',
  },
  errorMessageText: {
    color: theme.isDarkMode ? '#ff8a80' : '#C62828',
    backgroundColor: 'transparent',
  },
  subtitle: {
    fontSize: 14,
    fontStyle: 'italic',
    color: theme.colors.text,
    marginBottom: 30,
    lineHeight: 20,
    opacity: 0.8,
  },
  radioGroup: {
    flexDirection: 'row',
    marginBottom: 20,
    gap: 20,
  },
  radioOption: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 15,
  },
  radioCircle: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: theme.colors.primary || '#d4af37',
    marginRight: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioInner: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: theme.colors.primary || '#d4af37',
  },
  radioLabel: {
    fontSize: 16,
    color: theme.colors.text,
  },
  dropdownContainer: {
    marginBottom: 20,
    position: 'relative',
    zIndex: 10000,
    ...(Platform.OS === 'web' ? {
      overflow: 'visible',
    } : {}),
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: 8,
  },
  dropdownButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
    padding: 15,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  dropdownButtonText: {
    fontSize: 16,
    color: theme.colors.text,
    flex: 1,
    backgroundColor: 'transparent',
  },
  dropdownArrow: {
    fontSize: 12,
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  dropdownOverlay: {
    ...(Platform.OS === 'web' ? {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'transparent',
      zIndex: 9999,
    } : {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'transparent',
      zIndex: 9999,
    }),
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    marginTop: 5,
    maxHeight: 200,
    zIndex: 10001,
    elevation: 10001,
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
      pointerEvents: 'auto',
    } : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.5,
      shadowRadius: 8,
      elevation: 10,
    }),
  },
  dropdownScrollView: {
    maxHeight: 200,
  },
  dropdownOption: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  dropdownOptionText: {
    fontSize: 16,
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  createNewOption: {
    backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#F0F0F0',
  },
  createNewText: {
    fontSize: 16,
    color: theme.colors.primary || '#d4af37',
    fontWeight: '600',
    backgroundColor: 'transparent',
  },
  toggleButton: {
    backgroundColor: theme.colors.primary || '#d4af37',
    padding: 15,
    borderRadius: 8,
    marginBottom: 20,
    alignItems: 'center',
  },
  toggleButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  tableContainer: {
    marginBottom: 30,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F9F9F9',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: theme.isDarkMode ? '#333' : '#F5F5F5',
    borderBottomWidth: 2,
    borderBottomColor: theme.colors.primary || '#d4af37',
  },
  tableHeaderCell: {
    flex: 1,
    padding: 12,
    minWidth: 120,
    borderRightWidth: 1,
    borderRightColor: theme.colors.border,
  },
  tableHeaderText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  tableBody: {
    maxHeight: 400,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
  },
  tableCell: {
    flex: 1,
    padding: 12,
    minWidth: 120,
    borderRightWidth: 1,
    borderRightColor: theme.colors.border,
    justifyContent: 'center',
  },
  tableCellText: {
    fontSize: 14,
    color: theme.colors.text,
  },
  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 15,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F9F9F9',
  },
  paginationButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: theme.colors.primary || '#d4af37',
  },
  paginationButtonDisabled: {
    backgroundColor: '#999',
    opacity: 0.5,
  },
  paginationButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  paginationText: {
    fontSize: 14,
    color: theme.colors.text,
  },
  formContainer: {
    marginTop: 30,
    marginBottom: 40,
    ...(Platform.OS === 'web' ? {
      overflow: 'visible',
      position: 'relative',
    } : {}),
  },
  formTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.heading,
    marginBottom: 20,
  },
  formField: {
    marginBottom: 20,
    position: 'relative',
    zIndex: 1,
    ...(Platform.OS === 'web' ? {
      overflow: 'visible',
    } : {}),
  },
  formFieldDropdownOpen: {
    zIndex: 10000,
    elevation: 10000,
  },
  input: {
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
    borderRadius: 8,
    padding: 15,
    fontSize: 16,
    color: theme.colors.text,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  newWeaponContainer: {
    marginTop: 10,
  },
  newWeaponButtons: {
    flexDirection: 'row',
    marginTop: 10,
    gap: 10,
  },
  addWeaponButton: {
    flex: 1,
    backgroundColor: theme.colors.primary || '#d4af37',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  addWeaponButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  cancelWeaponButton: {
    flex: 1,
    backgroundColor: '#999',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelWeaponButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  addMultipleButton: {
    backgroundColor: '#d4af37', // Yellow background
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 15,
  },
  addMultipleButtonText: {
    color: '#000000', // Black text
    fontSize: 16,
    fontWeight: '600',
  },
  multipleBreaksFormContainer: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F9F9F9',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  multipleBreakFormRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginBottom: 15,
    gap: 10,
  },
  multipleBreakFormField: {
    flex: 1,
  },
  addFormRowButton: {
    backgroundColor: '#999999', // Grey background
    width: 40,
    height: 40,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  addFormRowButtonText: {
    color: '#FFFFFF',
    fontSize: 24,
    fontWeight: 'bold',
  },
  submitButton: {
    backgroundColor: theme.colors.primary || '#d4af37',
    padding: 18,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 20,
  },
  submitButtonDisabled: {
    opacity: 0.5,
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '600',
  },
  successMessageContainer: {
    backgroundColor: '#d4af37',
    padding: 15,
    borderRadius: 8,
    marginTop: 20,
    alignItems: 'center',
  },
  successMessageText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000000',
  },
  emptyTableMessage: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTableText: {
    fontSize: 16,
    color: theme.colors.text,
    textAlign: 'center',
    fontStyle: 'italic',
    opacity: 0.7,
  },
});
